"use client";
/**
 * Client hook consuming the authenticated SSE stream (/api/events/stream).
 *
 * Opens an EventSource (cookies ride along automatically → the server gates the
 * subscription to the user's OWN channels), dispatches each named event to an
 * optional handler map, and tracks connection status. EventSource auto-reconnects
 * on drop; pass an `onOpen` to re-fetch authoritative state on (re)connect —
 * that is how the wallet balance self-heals after a missed push.
 *
 * @example
 *   const { status } = useServerEvents({
 *     'wallet.balance': (d) => setBalance(d.balance as number),
 *     'demo.tick':      (d) => setTick(d.n as number),
 *   }, { onOpen: refetchBalance });
 */
import { useEffect, useRef, useState } from "react";
export function useServerEvents(handlers, opts = {}) {
    const { path = "/api/events/stream", onOpen } = opts;
    const [status, setStatus] = useState("connecting");
    // Keep the latest handlers/onOpen without re-opening the stream on every render.
    const handlersRef = useRef(handlers);
    handlersRef.current = handlers;
    const onOpenRef = useRef(onOpen);
    onOpenRef.current = onOpen;
    useEffect(() => {
        const es = new EventSource(path);
        es.onopen = () => {
            setStatus("open");
            onOpenRef.current?.();
        };
        es.onerror = () => setStatus("connecting"); // EventSource retries on its own.
        // Subscribe to every named event the caller cares about + the generic one.
        const names = new Set(Object.keys(handlersRef.current));
        const listeners = [];
        const attach = (name) => {
            const fn = (e) => {
                let data = {};
                try {
                    data = JSON.parse(e.data);
                }
                catch {
                    /* non-JSON frame — pass empty */
                }
                handlersRef.current[name]?.(data);
            };
            es.addEventListener(name, fn);
            listeners.push([name, fn]);
        };
        names.forEach(attach);
        return () => {
            setStatus("closed");
            listeners.forEach(([name, fn]) => es.removeEventListener(name, fn));
            es.close();
        };
        // Re-open only if the endpoint path changes.
    }, [path]);
    return { status };
}
